import { getFirebaseSingleton } from "@lib/firebase";
import { onLoggedOut } from "@src/app/store/session";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

export const useFirebaseListener = (pathname: string, email: string) => {
  const dispatch = useDispatch();

  const isDebuggingEnabled = useCbhFlag(CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING, {
    defaultValue: false,
  });

  const [isAuthenticated, setAuthenticated] = useState(false);

  const queryClient = useQueryClient();

  // Log on hook initialisation (TODO: remove after investigation - Bhavesh Songara)
  useLogEffect(APP_V2_APP_EVENTS.FIREBASE_LISTENER_HOOK_INITIALIZED, { pathname, email });

  useEffect(() => {
    // Log when the useEffect runs (TODO: remove after investigation - Bhavesh Songara)
    logEvent(APP_V2_APP_EVENTS.FIREBASE_LISTENER_USE_EFFECT_EXECUTED, { pathname, email });

    const handleIdTokenChanged = async (user: any) => {
      try {
        logEvent(APP_V2_APP_EVENTS.FIREBASE_ID_TOKEN_CHANGED, {
          isUserAlreadyAuthenticated: isAuthenticated,
          firebaseEmail: user?.email,
          email,
          userLastSignInTime: user?.metadata.lastSignInTime,
        });

        const isInitAutoLogin = localStorage.getItem("isInitAutoLogin");
        localStorage.removeItem("isInitAutoLogin");

        if (
          isInitAutoLogin === "true" &&
          user &&
          pathname === "/welcome/login/emailVerify" &&
          !email
        ) {
          // this will return in signin page, and not automatically login
          await getFirebaseSingleton().signOut();
          indexedDB.deleteDatabase("firebaseLocalStorage");
          return;
        }

        setAuthenticated(Boolean(user));

        if (user) {
          logEvent(APP_V2_APP_EVENTS.SIGNED_IN_TO_FIREBASE);

          await invalidateGetWorkerQuery(queryClient);
        } else {
          setTimeout(async () => {
            logEvent(APP_V2_APP_EVENTS.SIGNED_OUT_FROM_FIREBASE);
            setAuthenticated(false);

            // clear worker stored in api hook
            await queryClient.cancelQueries([GET_WORKER_QUERY_KEY]);
            queryClient.removeQueries([GET_WORKER_QUERY_KEY]);
            onLoggedOut(dispatch);
          }, 1000);
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.FIREBASE_LISTENER_ON_HANDLE_TOKEN_CHANGE_ERROR, {
          error,
          metadata: {
            email,
          },
        });

        // If we throw at any point after the firebase token
        // has been changed log the user out
        if (isDebuggingEnabled) {
          onLoggedOut(dispatch);
        }
      }
    };

    if (isAuthenticated) {
      logEvent(APP_V2_APP_EVENTS.FIREBASE_TOKEN_LISTENER_ATTACHED_AFTER_USER_WAS_AUTHENTICATED, {
        email: getFirebaseSingleton().currentUser?.email,
      });
    }

    if (pathname === "/welcome/login/emailVerify" && !email) {
      localStorage.setItem("isInitAutoLogin", "true");
    }

    // Log current user state before attaching the ID-token listener (Bhavesh Songara debug)
    try {
      const { currentUser } = getFirebaseSingleton();

      if (currentUser) {
        // Found a signed-in Firebase user
        logEvent(APP_V2_APP_EVENTS.FIREBASE_LISTENER_CURRENT_USER_BEFORE_LISTENER, {
          pathname,
          email: currentUser.email,
          uid: currentUser.uid,
          lastSignInTime: currentUser.metadata?.lastSignInTime,
          expirationTime: (currentUser as any)?.stsTokenManager?.expirationTime,
        });
      } else {
        // No user in Firebase cache
        logEvent(APP_V2_APP_EVENTS.FIREBASE_LISTENER_CURRENT_USER_NOT_FOUND_BEFORE_LISTENER, {
          pathname,
          providedEmail: email,
        });
      }
    } catch (error) {
      logError(APP_V2_APP_EVENTS.FIREBASE_LISTENER_CURRENT_USER_BEFORE_LISTENER_ERROR, {
        error,
        metadata: { stage: "before-listener", email },
      });
    }

    try {
      getFirebaseSingleton().onIdTokenChanged(handleIdTokenChanged, (error) => {
        logError(APP_V2_APP_EVENTS.FIREBASE_ON_ID_TOKEN_CHANGED_ERROR, {
          error,
        });
      });
      logEvent(APP_V2_APP_EVENTS.FIREBASE_ON_ID_TOKEN_CHANGED_LISTENER_ADDED);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.FIREBASE_LISTENER_HANDLER_ATTACHMENT_ERROR, {
        error,
        metadata: {
          email,
        },
      });
    }

    /**
     * This function should run once, but it depends on pathname and an email in the queryString.
     * We don't want to execute it for every path change, that's why ignoring rule-of-hooks here.
     * I believe we should refactor to not use pathname, but rather `window.location`
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isAuthenticated;
};
